import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'

// useLivePulse — the polling primitive for the Live View surface
// (spec 33 §7, PR L2). Polls every 30s with `staleTime: 0` so each
// refetch round-trips the server. Cache key `['live-pulse', eventId]`
// so the same payload feeds LiveTab (preview) and LiveDayView (full,
// PR L3) without duplicate requests.
//
// Returns the query result alongside a derived `secondsSinceUpdate`
// that ticks every second — the indicator dot animates without
// triggering a refetch. When the query is pending or errored
// `secondsSinceUpdate` is null and the caller renders the loading /
// error state directly.
//
// Hooks always unconditional (engineering lessons §2) — no early
// returns before the useState / useEffect calls. The 1s ticker is set
// up regardless of query state and is a no-op until `lastUpdatedAt`
// changes.

const POLL_MS = 30_000

export interface UseLivePulseResult {
  data: ReturnType<typeof useLivePulseQuery>['data']
  isLoading: boolean
  isError: boolean
  error: Error | null
  secondsSinceUpdate: number | null
  refetch: () => void
}

function useLivePulseQuery(eventId: number) {
  return useQuery({
    queryKey: ['live-pulse', eventId],
    queryFn: () => ApiClient.livePulse(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    refetchInterval: POLL_MS,
    staleTime: 0
  })
}

export function useLivePulse(eventId: number): UseLivePulseResult {
  const q = useLivePulseQuery(eventId)

  // 1s wall-clock ticker driving the header indicator state machine.
  // Resets to 0 every time `lastUpdatedAt` advances; clean-up on unmount
  // prevents the interval leaking.
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000)
    return () => window.clearInterval(id)
  }, [])

  let secondsSinceUpdate: number | null = null
  if (q.data?.lastUpdatedAt) {
    const ts = new Date(q.data.lastUpdatedAt).getTime()
    if (!Number.isNaN(ts)) {
      // Use the tick value so the dependency tracking re-renders this
      // component every second — otherwise the indicator would freeze
      // until the next refetch.
      void tick
      secondsSinceUpdate = Math.max(0, Math.round((Date.now() - ts) / 1000))
    }
  }

  return {
    data: q.data,
    isLoading: q.isLoading,
    isError: q.isError,
    // react-query 5 widens the error to `unknown`; the api wrapper only
    // ever throws Error, so narrow at the boundary.
    error: (q.error as Error | null) ?? null,
    secondsSinceUpdate,
    refetch: () => { void q.refetch() }
  }
}

export function useLiveSignOn(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => ApiClient.liveSignOn(eventId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['live-pulse', eventId] })
  })
}

export function useLiveSignOff(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => ApiClient.liveSignOff(eventId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['live-pulse', eventId] })
  })
}
